import { Listing } from '@root/types';
import { Link } from 'react-router-dom';
import { Button } from '@viewthespace/components';
import { useTranslation } from 'react-i18next';
import { useBreakpoint } from '@shared/useBreakpoints';
import routes from '@root/routes';
import useAltText from '@root/shared/useAltText';
import ActionButtons from './ActionButtons';

type Props = { listing: Listing };

const ListingLocation = ({ listing }: Props) => {
  const {
    building: { neighborhood: buildingNeighborhood, slug: buildingSlug },
    name: listingName,
    address: listingAddress,
  } = listing;
  const { t } = useTranslation('listing');
  const { isMobile } = useBreakpoint();

  const { getAltText } = useAltText({ address: listingAddress });

  const linkToBuildingPage = routes.building(buildingSlug);

  const { lettermarkLogo } = listing.building;

  const buildingPhoto =
    lettermarkLogo ||
    listing.building.photo ||
    listing.photos.find(photo => photo.type === 'staticMap');

  const renderBuildingPhoto = () => {
    return (
      <img
        className="h-[90px] w-[90px] rounded-[3px] border border-solid border-text-primary object-cover object-center"
        alt={
          lettermarkLogo
            ? getAltText({ type: 'buildingLettermarkLogo' })
            : getAltText({ type: buildingPhoto!.type })
        }
        src={buildingPhoto!.smallPath}
      />
    );
  };

  const renderCallsToAction = () => {
    return (
      <div className="flex gap-1 desktop:pt-1 tablet:pt-1 mobile:mt-auto">
        {listing?.spaceProvider?.slug && (
          <Link
            to={routes.landlords(listing.spaceProvider.slug)}
            target="_blank"
            data-testid="view-landlord-button"
          >
            <Button
              className="!border-solid"
              variant="secondary"
              size="small"
              text={t('viewPortfolio')}
              ariaLabel="view-portfolio-button"
              shape="pill"
            />
          </Link>
        )}

        <Link
          to={routes.building(listing.building.slug)}
          target="_blank"
          data-testid="view-building-button"
        >
          <Button
            className="!border-solid"
            variant="secondary"
            size="small"
            text={t('viewBuilding')}
            ariaLabel="view-building-button"
            shape="pill"
          />
        </Link>
      </div>
    );
  };

  return listing.hideLocation ? (
    <div className="mb-2 mobile:mt-2">
      <div className="flex w-full justify-between">
        {!!buildingPhoto && (
          <div className="mb-1 cursor-pointer " data-qa-testid="building-logo">
            {renderBuildingPhoto()}
          </div>
        )}
        <div className="grow" />
      </div>
      <h1 className="font-subtitle">
        {t('location.neighborhoodInShare', { neighborhood: buildingNeighborhood })}
      </h1>
      {listing.status === 'off_market' ? (
        <div className="inline-block rounded-[3px] border-2 border-solid border-black-015 px-[12px] py-1 text-coral font-body-medium">
          {t('offMarket')}
        </div>
      ) : null}
    </div>
  ) : (
    <div className="my-2 desktop:mt-0 tablet:mt-0" data-testid="listingLocation">
      <div className="flex w-full justify-between">
        <Link
          to={linkToBuildingPage}
          data-testid="buildingPhotoThumbnail"
          data-qa-testid="building-logo"
        >
          {!!buildingPhoto && (
            <figure className="cursor-pointer desktop:mb-2 tablet:mb-2">
              {renderBuildingPhoto()}
            </figure>
          )}
        </Link>
        <div className="flex flex-col items-end">
          <ActionButtons listing={listing} />
          {isMobile && renderCallsToAction()}
        </div>
      </div>
      {isMobile && <hr className="my-2 text-general-neutral-tertiary" />}
      <div>
        <h1 className="!m-0 font-body-large-emphasis" data-qa-testid="building-name">
          <ul>
            <div>{listing.address.buildingName} </div>
            <div>{listing.address.street}</div>
          </ul>
        </h1>
      </div>
      <section data-qa-testid="listing-address">
        <div>
          <h2 className="m-0 flex flex-wrap items-center font-body-large">{listingName}</h2>
        </div>
        <div>
          <h2 className="m-0 flex flex-wrap items-center font-body-large">
            {buildingNeighborhood ? (
              <>
                {buildingNeighborhood}
                <figure className="mx-1 my-0 h-[16px] w-[1px] bg-black-015"></figure>{' '}
              </>
            ) : null}
            {t('common:cityLocalityRegion', { address: listing.address })}
          </h2>
        </div>
        <div>
          <h2 className="m-0 flex flex-wrap items-center font-body-large">
            {t(`country:${listing.address.countryCode}`)}
          </h2>
        </div>
        {listing.address.crossStreets ? (
          <div className="font-body-medium">
            {t('crossStreets', { crossStreets: listing.address.crossStreets })}
          </div>
        ) : null}
      </section>
      {listing.status === 'off_market' ? (
        <div className="inline-block rounded-[3px] border-2 border-solid border-black-015 px-[12px] py-1 text-coral font-body-medium">
          {t('offMarket')}
        </div>
      ) : null}
      {!isMobile && renderCallsToAction()}
    </div>
  );
};

export default ListingLocation;
